import { authClient } from "lib/auth/authClient";
import * as tracking from "lib/tracking";
import { getCSRFToken } from "lib/utils";

export const SIGN_OUT_REDIRECT_PATH =
  process.env.NODE_ENV === "development" ? "http://localhost:3000" : "https://app.stotles.com";

// Cap the best-effort better-auth logout so an unreachable auth-service can't hang the Rails logout;
// better-fetch aborts the request after this and the rejection is handled below.
export const BETTER_AUTH_LOGOUT_TIMEOUT_MS = 3000;

/**
 * Best-effort: end the better-auth session, if there is one, before the Rails logout. Never blocks
 * it — a failure or timeout goes to Sentry and logout proceeds.
 */
const logoutBetterAuth = async (): Promise<void> => {
  const fetchOptions = { timeout: BETTER_AUTH_LOGOUT_TIMEOUT_MS };
  try {
    const { data } = await authClient.getSession({ fetchOptions });
    if (data) {
      await authClient.signOut({ fetchOptions });
    }
  } catch (error) {
    console.log("Failed to logout from better-auth session; proceeding with Rails logout", error);

    // TODO: Uncomment Sentry capture once better-auth has been deployed in production, also uncomment in tests
    // Sentry.captureException(error);
  }
};

/** Signs out of better-auth and Rails, then goes to `destination` or the default sign-out page. */
const logout = async (destination?: string): Promise<void> => {
  const csrfToken = getCSRFToken() || "";
  tracking.logout();
  await logoutBetterAuth();
  await fetch("/users/sign_out", {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken },
  });
  window.location.href = destination || SIGN_OUT_REDIRECT_PATH;
};

export const performLogout = (): Promise<void> => logout();

/** Logs out, then straight to the login page, which sends the user back to `redirect` after. */
export const performLogoutToLogin = (redirect: string): Promise<void> =>
  logout(`/auth/login?redirect=${encodeURIComponent(redirect)}`);
